import { api } from "utils/api";

/**
 * Parse route/param customer_id (may be negative, e.g. -1 placeholder accounts).
 * @param {string | number | null | undefined} customerId
 * @returns {number}
 */
export function parseCustomerId(customerId) {
  const parsed = Number.parseInt(String(customerId ?? ""), 10);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}

/**
 * Persist one outbound communication row for a customer.
 * @param {{ customerId: string | number, destination: string, message: string }} params
 * @returns {Promise<object>}
 */
export async function logCustomerCommunication({ customerId, destination, message }) {
  const cid = parseCustomerId(customerId);
  if (!isValidCustomerId(cid)) {
    return { success: false, inserted: 0, skipped: 1 };
  }
  const destinationText = String(destination || "").trim() || "unknown";
  const messageText = String(message || "").trim();
  if (!messageText) {
    return { success: false, inserted: 0, skipped: 1 };
  }
  try {
    return await api.post("/db/customers/communications/log", {
      customer_id: cid,
      destination: destinationText,
      message: messageText,
    });
  } catch (err) {
    console.warn("Failed to log customer communication:", err);
    return { success: false, inserted: 0, skipped: 1 };
  }
}

/**
 * CRM customer ids are valid when finite and not zero (negative ids such as -1 are allowed).
 * @param {string | number | null | undefined} customerId
 * @returns {boolean}
 */
export function isValidCustomerId(customerId) {
  const parsed = parseCustomerId(customerId);
  return Number.isFinite(parsed) && parsed !== 0;
}

/**
 * Merged customer contacts via `customer_contact_links` + `customer_contacts` filter APIs.
 * Same shape as `GET /db/customers/:id/contacts` (works when that route is unavailable).
 * @param {string | number} customerId
 * @returns {Promise<object[]>}
 */
export async function fetchCustomerMergedContacts(customerId) {
  const cid = parseCustomerId(customerId);
  if (!isValidCustomerId(customerId)) return [];

  const linksResponse = await api.post("/db/customer_contact_links/filter", {
    filters: { customer_id: cid },
  });
  const links = Array.isArray(linksResponse) ? linksResponse : [];
  if (!links.length) return [];

  const linkByContactId = new Map();
  for (const link of links) {
    const contactId = Number(link?.customer_contact_id);
    if (contactId > 0) linkByContactId.set(contactId, link);
  }

  const contactIds = [...linkByContactId.keys()];
  if (!contactIds.length) return [];

  const contactsResponse = await api.post("/db/customer_contacts/filter", {
    filters: { customer_contact_id: contactIds },
  });
  const contacts = Array.isArray(contactsResponse) ? contactsResponse : [];

  return contacts.map((contact) => {
    const contactId = Number(contact?.customer_contact_id || 0);
    const link = linkByContactId.get(contactId) || {};
    return {
      ...contact,
      primary: link.primary_contact ?? "no",
      get_email: link.get_email ?? "no",
      get_whatsapp: link.get_whatsapp ?? "no",
      get_billing: link.get_billing ?? "no",
      get_notification: link.get_notification ?? "no",
    };
  });
}
